import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { registrationService } from '../services/registrationService';
import { configService } from '../services/configService';

const { FiUpload, FiShield, FiCopy, FiCheck, FiCreditCard, FiMessageCircle, FiTrendingUp, FiImage, FiX } = FiIcons;

// แผนผังลิงก์ Stripe ตามช่วงเวลา
const STRIPE_LINKS = {
  tier2: "https://buy.stripe.com/5kQcN6f8Uan6gEX7AA1oI08", // 9,500 (ปัจจุบัน)
  tier3: "https://buy.stripe.com/eVq6oIbWIeDm4Wf8EE1oI09", // 10,500 (เริ่ม 9 ส.ค. 69)
  tier4: "https://buy.stripe.com/eVq14o5ykeDm1K34oo1oI0a", // 11,500 (เริ่ม 19 ส.ค. 69)
  tier5: "https://buy.stripe.com/6oUcN68KwgLugEXf321oI0b"  // 12,900 (เริ่ม 15 ก.ย. 69)
};

export default function Registration() {
  const [formData, setFormData] = useState({ fullName: '', nickname: '', phone: '', email: '', remarks: '' });
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // คำนวณลิงก์ Stripe ที่ถูกต้องตามเวลาปัจจุบัน
  const activeStripeLink = useMemo(() => {
    const now = new Date();
    const t3Date = new Date('2026-08-09T00:00:00');
    const t4Date = new Date('2026-08-19T00:00:00');
    const t5Date = new Date('2026-09-15T00:00:00');

    if (now >= t5Date) return STRIPE_LINKS.tier5;
    if (now >= t4Date) return STRIPE_LINKS.tier4;
    if (now >= t3Date) return STRIPE_LINKS.tier3;
    return STRIPE_LINKS.tier2;
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.phone.length !== 10) return alert("กรุณากรอกเบอร์โทรศัพท์ให้ครบ 10 หลัก");
    if (paymentMethod === 'bank' && !selectedFile) return alert("กรุณาอัปโหลดสลิปโอนเงิน");

    setIsLoading(true);
    try {
      // บันทึกลง Google Sheet ก่อน (สถานะ Pending)
      const id = await registrationService.create({ ...formData, paymentMethod }, selectedFile);
      
      if (paymentMethod === 'stripe') {
        // เก็บ ID ไว้ในเครื่องเพื่อใช้ยืนยันในหน้า Success
        localStorage.setItem('pending_reg_id', id);
        // ไปที่ Stripe พร้อมแนบอีเมล
        const finalUrl = `${activeStripeLink}?prefilled_email=${encodeURIComponent(formData.email)}`;
        window.location.href = finalUrl;
      } else {
        setIsSubmitted(true);
      }
    } catch (error) {
      alert(error.message || 'เกิดข้อผิดพลาด');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="registration" className="section-padding bg-zinc-50 text-zinc-900 relative overflow-hidden border-t border-zinc-200">
      <div className="max-w-7xl mx-auto relative z-10 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="sticky top-32">
              <span className="text-brand-red font-black uppercase tracking-[0.3em] text-xs mb-4 block">Registration & Payment</span>
              <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-8 leading-tight tracking-tighter">
                เริ่มต้นเส้นทาง <br /> <span className="text-brand-red">BAZI FINANCIAL & INVESTMENT</span>
              </h2>
              <div className="space-y-6 mb-12">
                <div className="bg-white border-2 border-zinc-900 p-8 shadow-[15px_15px_0px_0px_rgba(0,0,0,0.05)]">
                  <h4 className="text-zinc-900 font-black text-xl mb-6 uppercase tracking-tight flex items-center gap-3">
                    <SafeIcon icon={FiShield} className="text-brand-red" /> ยืนยันที่นั่งของคุณ
                  </h4>
                  <p className="text-zinc-600 font-medium leading-relaxed">
                    กรุณากรอกข้อมูลเพื่อรับสิทธิ์ซอฟต์แวร์วิเคราะห์ดวงและเอกสารประกอบการเรียน
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white border-2 border-zinc-200 p-8 md:p-16 shadow-2xl">
              <div className="mb-12">
                <h3 className="text-4xl font-black mb-4 tracking-tight">ลงทะเบียนเข้าร่วม</h3>
                <p className="text-zinc-900 text-xl font-bold tracking-tight">จองที่นั่งของคุณตอนนี้</p>
              </div>

              <form className="space-y-8" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="block text-zinc-900 text-xs font-black uppercase tracking-widest">ชื่อ-นามสกุล *</label>
                    <input type="text" className="w-full bg-zinc-50 border-b-2 border-zinc-200 p-4 focus:border-brand-red focus:bg-white focus:outline-none transition-all font-medium text-lg" placeholder="ระบุชื่อจริง" required value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-zinc-900 text-xs font-black uppercase tracking-widest">ชื่อเล่น *</label>
                    <input type="text" className="w-full bg-zinc-50 border-b-2 border-zinc-200 p-4 focus:border-brand-red focus:bg-white focus:outline-none transition-all font-medium text-lg" placeholder="ระบุชื่อเล่น" required value={formData.nickname} onChange={(e) => setFormData({ ...formData, nickname: e.target.value })} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="block text-zinc-900 text-xs font-black uppercase tracking-widest">เบอร์โทรศัพท์ *</label>
                    <input type="tel" className="w-full bg-zinc-50 border-b-2 border-zinc-200 p-4 focus:border-brand-red focus:bg-white focus:outline-none transition-all font-medium text-lg" placeholder="09XXXXXXXX" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })} />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-zinc-900 text-xs font-black uppercase tracking-widest">อีเมล *</label>
                    <input type="email" className="w-full bg-zinc-50 border-b-2 border-zinc-200 p-4 focus:border-brand-red focus:bg-white focus:outline-none transition-all font-medium text-lg" placeholder="example@email.com" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                </div>

                <div className="pt-8 border-t border-zinc-100">
                  <label className="block text-zinc-900 text-xs font-black uppercase tracking-widest mb-6">เลือกช่องทางการชำระเงิน *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button type="button" onClick={() => setPaymentMethod('bank')} className={`flex items-center justify-center gap-3 p-6 border-2 transition-all font-black uppercase tracking-widest text-sm ${paymentMethod === 'bank' ? 'border-brand-red bg-brand-red/5 text-brand-red' : 'border-zinc-200 text-zinc-400 hover:border-zinc-300'}`}>
                      <SafeIcon icon={FiCopy} className="w-5 h-5" /> โอนผ่านธนาคาร
                    </button>
                    <button type="button" onClick={() => setPaymentMethod('stripe')} className={`flex items-center justify-center gap-3 p-6 border-2 transition-all font-black uppercase tracking-widest text-sm ${paymentMethod === 'stripe' ? 'border-brand-red bg-brand-red/5 text-brand-red' : 'border-zinc-200 text-zinc-400 hover:border-zinc-300'}`}>
                      <SafeIcon icon={FiCreditCard} className="w-5 h-5" /> Stripe / บัตรเครดิต
                    </button>
                  </div>
                </div>

                {paymentMethod === 'bank' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-6 pt-6">
                    <div className="bg-zinc-900 text-white p-8 relative overflow-hidden">
                      <p className="text-white/50 font-black uppercase tracking-widest text-[10px] mb-4">SCB Bank</p>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-3xl md:text-4xl font-black tracking-tighter">149-229-8-558</p>
                        <button type="button" onClick={() => { navigator.clipboard.writeText('1492298558'); setIsCopied(true); setTimeout(() => setIsCopied(false), 2000); }} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black transition-all ${isCopied ? 'bg-green-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                          <SafeIcon icon={isCopied ? FiCheck : FiCopy} className="w-3 h-3" /> {isCopied ? 'COPIED' : 'COPY'}
                        </button>
                      </div>
                      <p className="font-bold text-lg">ชื่อบัญชี: รวิ อัญญากาญจน์</p>
                    </div>
                    <div className={`relative group cursor-pointer border-2 border-dashed transition-all p-8 text-center ${selectedFile ? 'border-green-500 bg-green-50/10' : 'border-zinc-300 hover:border-brand-red bg-zinc-50'}`}>
                      <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer z-10" onChange={handleFileChange} required={paymentMethod === 'bank'} />
                      {previewUrl ? (
                        <div className="relative flex flex-col items-center">
                          <div className="relative w-32 h-40 mb-4 border-2 border-white shadow-lg overflow-hidden bg-white">
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                            <button type="button" onClick={removeFile} className="absolute top-1 right-1 w-6 h-6 bg-brand-red text-white rounded-full flex items-center justify-center shadow-md hover:scale-110" >
                              <SafeIcon icon={FiX} className="w-3 h-3" />
                            </button>
                          </div>
                          <p className="font-black text-green-700 text-xs uppercase tracking-widest truncate max-w-[200px]">{selectedFile.name}</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 rounded-full flex items-center justify-center border bg-white border-zinc-200 mb-4">
                            <SafeIcon icon={FiImage} className="w-6 h-6 text-zinc-400" />
                          </div>
                          <p className="font-black uppercase tracking-widest text-xs text-zinc-900">อัปโหลดหลักฐานการโอนเงิน</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                <button disabled={isLoading} className={`w-full py-6 text-white font-black text-lg transition-all duration-500 uppercase tracking-[0.2em] shadow-xl flex items-center justify-center gap-4 ${isLoading ? 'bg-zinc-400' : 'bg-zinc-900 hover:bg-brand-red'}`}>
                  {isLoading ? 'กำลังบันทึกข้อมูล...' : (paymentMethod === 'stripe' ? 'ไปที่หน้าชำระเงิน' : 'ยืนยันการลงทะเบียน')}
                  {!isLoading && <div className="w-6 h-[1px] bg-white group-hover:w-10 transition-all"></div>}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isSubmitted && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-zinc-900/90 backdrop-blur-md" />
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} className="bg-white max-w-lg w-full p-10 md:p-16 relative z-10 text-center border-4 border-brand-red shadow-2xl">
              <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8 border-2 border-green-500">
                <SafeIcon icon={FiCheck} className="w-12 h-12 text-green-500" />
              </div>
              <h3 className="text-3xl md:text-4xl font-black text-zinc-900 mb-6 tracking-tight">บันทึกข้อมูลสำเร็จ!</h3>
              <p className="text-zinc-600 text-lg font-medium leading-relaxed mb-10">ระบบได้เก็บหลักฐานการโอนเงินของคุณไว้เรียบร้อยแล้ว</p>
              <button onClick={() => { window.open(`https://line.me/R/oaMessage/@fengshuix/?${encodeURIComponent('ลงทะเบียน BAZI FINANCIAL เรียบร้อยแล้ว')}`, '_blank'); setIsSubmitted(false); }} className="w-full py-6 text-white font-black text-xl bg-[#06C755] hover:scale-105 transition-all shadow-xl flex items-center justify-center gap-4">
                <SafeIcon icon={FiMessageCircle} className="w-7 h-7" /> CONFIRM VIA LINE
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}